import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { theme } from '@styles/theme';

import { api } from 'api';

import { IntegrationIcon } from 'components/IntegrationIcon';
import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

import {
  HireflixErrorTypes,
  HireflixInterviewStats,
  HireflixNotification,
  HireflixPositionSuccess,
} from './types';
import { getErrorMessage, getNotificationText } from './utils';

interface Option {
  label: string;
  value: string;
}

type IntegrationParams = {
  jobID: string;
  integrationID: string;
};

const NoPositionsAvailable = observer(() => {
  const { t } = useStore();
  return <InfoBox text={t('integrations.hireflix.job.app.nopositions')} />;
});

const Form = observer(() => {
  const { t } = useStore();
  const { showError } = useToaster();
  const { jobID } = useParams() as IntegrationParams;
  const positionsQuery = useQuery({
    queryKey: ['hireflixPositions'],
    queryFn: api.integrations.hireflixPositions,
  });

  let options: Option[] = [];
  if (positionsQuery.isSuccess && !('error' in positionsQuery.data)) {
    options = positionsQuery.data.map((position) => ({
      label: position.name,
      value: position.id,
    }));
  }

  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const client = useQueryClient();

  function linkJobPosition(positionId: string, positionName: string) {
    setSaving(true);
    api.integrations
      .hireflixLinkJobPosition(jobID, positionId, positionName)
      .then(() => {
        client.resetQueries(['hireflixLinkedPosition']);
        setSaving(false);
      })
      .catch((e) => {
        showError(t(`common.errors.${e.response.status}`));
        setSaving(false);
      });
  }

  if (positionsQuery.isLoading) {
    return <Loading fixed />;
  } else if (!options.length) {
    return <NoPositionsAvailable />;
  } else if (positionsQuery.isSuccess) {
    return (
      <Card space={6} variant="transparent">
        <Stack space={6}>
          <Select
            label={t('integrations.hireflix.job.app.form.position.label')}
            options={options}
            onChange={(value) => setSelected(value)}
          />
          <Stack space={0} orientation="horizontal" justify="flex-end">
            <Button
              variant="primary"
              onClick={() =>
                linkJobPosition(
                  selected,
                  options.find((option) => option.value === selected)?.label || ''
                )
              }
              text={t('integrations.hireflix.job.app.form.save')}
              isDisabled={selected === '' || saving}
            />
          </Stack>
        </Stack>
      </Card>
    );
  }
  return <Loading fixed />;
});

const Info = observer((props: HireflixPositionSuccess) => {
  const { name, notifications, interviewStats } = props;
  const { t } = useStore();
  const { jobID } = useParams() as IntegrationParams;
  const client = useQueryClient();

  function removeLinkedJobPosition() {
    api.integrations.hireflixRemoveLinkedJobPosition(jobID).then(() => {
      client.resetQueries(['hireflixLinkedPosition']);
    });
  }
  const deletable =
    !interviewStats ||
    (interviewStats && interviewStats.totalCompleted === 0 && interviewStats.totalPending === 0);

  return (
    <Card space={6}>
      <Stack space={4}>
        <div>
          <Text font="semibold14" text={t('integrations.hireflix.job.app.info.position.label')} />
          <Text font="regular16" text={name} />
        </div>
        <Notifications notifications={notifications} />
        <InterviewStats interviewStats={interviewStats} />
      </Stack>
      {deletable && (
        <Stack space={0} orientation="horizontal" justify="flex-end">
          <Button
            variant="secondary"
            iconLeft={<IconTrash />}
            onClick={removeLinkedJobPosition}
            text={t('integrations.hireflix.job.app.info.remove.label')}
          />
        </Stack>
      )}
    </Card>
  );
});

interface NotificationsProps {
  notifications?: HireflixNotification[];
}

const Notifications = observer((props: NotificationsProps) => {
  const { notifications } = props;
  const { t } = useStore();
  if (!notifications) {
    return null;
  }
  return (
    <div>
      <Text font="semibold14" text={t('integrations.hireflix.job.app.info.notifications.label')} />
      <Stack space={2}>
        {notifications.map((notification, idx) => (
          <Text key={idx} font="regular16" text={getNotificationText(notification, t)} />
        ))}
      </Stack>
    </div>
  );
});

interface InterviewStatsProps {
  interviewStats?: HireflixInterviewStats;
}

const InterviewStats = observer((props: InterviewStatsProps) => {
  const { interviewStats } = props;
  const { t } = useStore();

  if (!interviewStats) {
    return null;
  }
  return (
    <>
      <div>
        <Text font="semibold14" text={t('integrations.hireflix.job.app.info.sent.label')} />
        <Text font="regular16" text={interviewStats.totalPending} />
      </div>
      <div>
        <Text font="semibold14" text={t('integrations.hireflix.job.app.info.completed.label')} />
        <Text
          font="regular16"
          text={
            interviewStats.totalCompleted +
            interviewStats.totalFinalist +
            interviewStats.totalArchived
          }
        />
      </div>
    </>
  );
});

interface ErrorProps {
  error: HireflixErrorTypes;
}

const Error = observer((props: ErrorProps) => {
  const { t } = useStore();
  const { error } = props;
  return <Card space={6}>{getErrorMessage(error, t)}</Card>;
});

export const HireflixJobApp = observer(() => {
  const { t } = useStore();
  const { jobID } = useParams() as IntegrationParams;

  const linkedPositionQuery = useQuery({
    queryKey: ['hireflixLinkedPosition'],
    queryFn: () => api.integrations.hireflixGetLinkedJobPosition(jobID),
  });

  let component = <Loading fixed />;
  let link = '';
  if (linkedPositionQuery.isSuccess) {
    if (linkedPositionQuery.data && 'error' in linkedPositionQuery.data) {
      component = <Error error={linkedPositionQuery.data.error} />;
    } else if (!linkedPositionQuery.data) {
      component = <Form />;
    } else {
      link = `https://admin.hireflix.com/en/jobs/${linkedPositionQuery.data.id}/`;
      component = (
        <Info
          name={linkedPositionQuery.data.name}
          id={linkedPositionQuery.data.id}
          notifications={linkedPositionQuery.data.notifications}
          interviewStats={linkedPositionQuery.data.interviewStats}
        />
      );
    }
  }

  return (
    <Stack space={6}>
      <Card space={6}>
        <Stack space={0} orientation="horizontal" align="center" justify="space-between">
          <IntegrationIcon integrationName={'Hireflix'} color="purple100" />
          {link && (
            <Link href={link} target="_blank">
              <IconOpenNewTab />
            </Link>
          )}
        </Stack>
        <Text color="purple100" font="bold16" text={'Hireflix'} />
        <Text
          color="purple100"
          font="regular14"
          text={t('integrations.hireflix.job.app.form.description')}
        />
      </Card>
      {component}
    </Stack>
  );
});

const Link = styled.a((p) => ({
  color: theme.color.purple50,
  ':hover': {
    color: theme.color.purple100,
  },
}));
